var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useCallback, useRef, useEffect, useMemo, memo } from 'react';
import { ImageIcon, ImageBrokenIcon } from '@move-ui/icons-asset-catalog';
import cx from 'classnames';
import Image, { IMAGE_FIT } from './Image';
import styles from './ImageLoader.module.css';
import { getImageObserver, isLazyLoadingSupported, isSSR } from './imageObserver';
export var IMAGE_STATUS;
(function (IMAGE_STATUS) {
    IMAGE_STATUS["INITIAL"] = "initial";
    IMAGE_STATUS["LOADING"] = "loading";
    IMAGE_STATUS["ERROR"] = "error";
    IMAGE_STATUS["LOADED"] = "loaded";
})(IMAGE_STATUS || (IMAGE_STATUS = {}));
export const ImageLoader = (_a) => {
    var { src = '', srcSet, title = '', className = '', placeholderRatio, fit = IMAGE_FIT.CONTAIN, borderRadius, reserveHeight = false, onImageLoaded, onImageError, renderCustomPlaceholder, lazyLoad = false, placeholderClassName = '' } = _a, rest = __rest(_a, ["src", "srcSet", "title", "className", "placeholderRatio", "fit", "borderRadius", "reserveHeight", "onImageLoaded", "onImageError", "renderCustomPlaceholder", "lazyLoad", "placeholderClassName"]);
    const hasSrc = Boolean(src);
    const [status, setStatus] = useState(IMAGE_STATUS.INITIAL);
    const imageRef = useRef(null);
    const useLazyLoading = (isLazyLoadingSupported || isSSR) && hasSrc && lazyLoad;
    useEffect(() => {
        const imageElement = imageRef.current;
        if (hasSrc && imageElement) {
            if (!imageElement.complete) {
                setStatus(IMAGE_STATUS.LOADING);
            }
            else if (imageElement.naturalHeight === 0 && !useLazyLoading) {
                // naturalHeight === 0 indicates that there was an error loading the
                // image.
                setStatus(IMAGE_STATUS.ERROR);
            }
        }
        if (useLazyLoading && imageElement) {
            getImageObserver().observe(imageElement);
        }
        return () => {
            if (useLazyLoading && imageElement) {
                getImageObserver().unobserve(imageElement);
            }
        };
    }, [src, hasSrc, useLazyLoading]);
    const handleImageLoaded = useCallback((event) => {
        onImageLoaded === null || onImageLoaded === void 0 ? void 0 : onImageLoaded(event);
        setStatus(IMAGE_STATUS.LOADED);
    }, [onImageLoaded]);
    const handleImageError = useCallback(() => {
        onImageError === null || onImageError === void 0 ? void 0 : onImageError();
        setStatus(IMAGE_STATUS.ERROR);
    }, [onImageError]);
    const placeholderDimensions = useMemo(() => {
        if (!placeholderRatio) {
            return {
                height: '100%'
            };
        }
        const { width = 1, height = 1 } = placeholderRatio;
        return {
            paddingTop: `${(100 / width) * height}%`
        };
    }, [placeholderRatio]);
    const hideImage = (status === IMAGE_STATUS.LOADING && !useLazyLoading) ||
        status === IMAGE_STATUS.ERROR;
    // don't show the placeholder on initial rendering
    // on initial rendering we serve only the <img> tag to make google happy
    // (except in the case of lazy loading)
    const hidePlaceholder = (hasSrc && status === IMAGE_STATUS.INITIAL && !useLazyLoading) ||
        status === IMAGE_STATUS.LOADED;
    return (React.createElement("div", { style: reserveHeight ? placeholderDimensions : {}, className: cx(styles.container, {
            [styles[`border_radius_${borderRadius}`]]: Boolean(borderRadius)
        }) },
        hasSrc && (React.createElement(Image, Object.assign({ title: title, className: className, isLoading: status === IMAGE_STATUS.LOADING ||
                (useLazyLoading && status === IMAGE_STATUS.INITIAL), reserveHeight: reserveHeight, hideImage: hideImage, fit: fit, useLazyLoading: useLazyLoading, src: src, srcSet: srcSet, handleImageLoaded: handleImageLoaded, handleImageError: handleImageError, imageRef: imageRef }, rest))),
        !hidePlaceholder && (React.createElement("div", { style: placeholderDimensions, className: cx(placeholderClassName, styles.placeholderContainer, {
                [styles.reserveHeight]: reserveHeight
            }) }, typeof renderCustomPlaceholder === 'function' ? (renderCustomPlaceholder(status)) : (React.createElement("div", { className: cx(styles.placeholder, {
                [styles.initial]: status === IMAGE_STATUS.INITIAL,
                [styles.loading]: status === IMAGE_STATUS.LOADING ||
                    (useLazyLoading && status === IMAGE_STATUS.INITIAL),
                [styles.error]: status === IMAGE_STATUS.ERROR
            }), "data-testid": "image-loader-placeholder" }, status === IMAGE_STATUS.ERROR ? (React.createElement(ImageBrokenIcon, { className: styles.icon })) : (React.createElement(ImageIcon, { className: styles.icon }))))))));
};
export default memo(ImageLoader);
//# sourceMappingURL=ImageLoader.js.map